import { cartStoreMap } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { useFpB2bPaymentService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-payment-service'
import { useFpCkoRedirectApmExecution } from '@cart-services/hooks/use-fp-cko-redirect-apm-execution'
import { AmountDetails } from '@cart-ui/AmountDetails/AmountDetails'
import { InvoiceInfo } from '@cart-ui/InvoiceInfo/InvoiceInfo'
import { MerchantInfo } from '@cart-ui/MerchantInfo/MerchantInfo'
import { OrderNote } from '@cart-ui/OrderNote/OrderNote'
import { PaymentInfo } from '@cart-ui/PaymentInfo/PaymentInfo'
import { ShippingInfo } from '@cart-ui/ShippingInfo/ShippingInfo'
import { DATE_FORMAT, TIME_FORMAT } from '@constants'
import { useStmAnalyticsEvent } from '@dni-analysis'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { useApiGetMemberPaymentTransactionRequest } from '@hooks-api/fortune/useApiGetMemberPaymentTransactionRequest'
import { ShippingMethodMap } from '@hooks-api/types/dataModel/shipping'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { CircularProgress } from '@pets-ui/CircularProgress/CircularProgress'
import { HStack } from '@pets-ui/HStack/HStack'
import { SuccessFilled } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { zIndex } from '@pets-ui/tokens/zIndex.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { format } from 'date-fns'
import { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom-v5'
import { useThankyou } from '../../fp-b2b-shared/hooks/use-thankyou'
import { useStmCampaignCode } from '../hooks'
import { CartReminder } from './components/CartReminder'
import { OrderInfo } from './components/OrderInfo'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  root: {
    backgroundColor: colors.grey100,
    minHeight: '100vh',
  },
  wrapper: {
    padding: {
      [MOBILE]: 16,
      default: 0,
    },
    display: 'flex',
    justifyContent: 'center',
  },
  container: {
    padding: {
      [MOBILE]: 0,
      default: 16,
    },
    overflow: 'auto',
    backgroundColor: colors.grey100,
    marginBottom: {
      [MOBILE]: 68,
      default: 0,
    },
    width: 400,
  },
  footer: {
    padding: {
      [MOBILE]: '10px 10px',
      default: '10px 140px',
    },
    alignItems: 'center',
    backgroundColor: colors.white,
    display: 'flex',
    justifyContent: 'center',
    position: 'fixed',
    zIndex: zIndex.appBar,
    bottom: 0,
    left: 0,
    right: 0,
  },
  infoBox: {
    padding: 16,
    borderRadius: 8,
    gap: 8,
    backgroundColor: colors.white,
  },
})

interface ThankyouProps {
  basePath: string
}

export function Thankyou({ basePath }: ThankyouProps) {
  const isMobile = useResponsive('mobile')
  const history = useHistory()
  const params = useParams<{ transactionId: string }>()
  const transactionId = params?.transactionId

  const {
    destructuredData: paymentTransaction,
    isLoading: isPaymentTransactionLoading,
    mutate,
  } = useApiGetMemberPaymentTransactionRequest({ id: transactionId })

  const ckoPaymentId = new URLSearchParams(window.location.search).get('cko-payment-id')
  const { isCkoExecuting } = useFpCkoRedirectApmExecution({
    ckoPaymentId,
    paymentTransactionId: transactionId,
    paymentSuccessUrl: `${window.location.origin}${basePath}/thankyou/${transactionId}`,
    paymentFallbackUrl: `${window.location.origin}${basePath}/checkout?type=failedPayment`,
    onReconciled: mutate,
  })

  const { campaignId, currency, platform } = useThankyou()
  const { campaignCode, projectCode } = useStmCampaignCode()
  const { destructuredData: userInfo } = useApiCampaignMember(campaignCode ? { code: campaignCode } : undefined)
  const { sendCrossBrandPurchaseEvent } = useStmAnalyticsEvent({ userId: userInfo?.platform_uid ?? '' })
  const { resetPaymentTransaction } = useFpB2bPaymentService({ campaignId, platform })
  const {
    subtotal,
    discount_total: discountTotal,
    shipping_total: shippingTotal,
    total,
    payment_method: paymentMethod,
    discount_items: discountItems,
  } = paymentTransaction || {}
  const formatCreatedDate = paymentTransaction?.created_at ? format(new Date(paymentTransaction?.created_at), DATE_FORMAT + ' ' + TIME_FORMAT) : ''
  const invoiceOption = paymentTransaction?.invoice_option
  const shippingMethodKey = paymentTransaction?.child_orders?.[0]?.shipping_method
  const shippingMethod = shippingMethodKey ? ShippingMethodMap[shippingMethodKey] : ''
  const { resetCartValues } = useFpB2bCartService(campaignId, platform)
  const { useSyncCartSessionToStore } = cartStoreMap[platform]
  const { hasFinishedSyncCartSession } = useSyncCartSessionToStore({ campaignCode: campaignCode ?? undefined, campaignId })

  const handleClickBackToHome = () => {
    history.push(`${basePath}/product-catalog`)
  }

  useEffect(function sendPurchaseTrackingOnLoad() {
    const transactionId = paymentTransaction?.transaction_id
    if (!transactionId || !projectCode || !userInfo?.platform_uid) return
    sendCrossBrandPurchaseEvent({ projectCode, transactionId })
  }, [paymentTransaction?.transaction_id, projectCode, userInfo?.platform_uid, sendCrossBrandPurchaseEvent])

  useEffect(function resetCartOnSync() {
    if (!hasFinishedSyncCartSession) return
    resetCartValues()
    resetPaymentTransaction()
  }, [hasFinishedSyncCartSession, resetCartValues, resetPaymentTransaction])

  return (
    <Box rootStyle={styles.root}>
      {
        isPaymentTransactionLoading || isCkoExecuting
          ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
              <CircularProgress fontSize={24} color={colors.grey300} />
            </Box>
          )
          : (
            <Box rootStyle={styles.wrapper}>
              <VStack gap={3} rootStyle={styles.container}>
                <HStack gap={4} alignItems="center" justifyContent="center" width="100%">
                  <SuccessFilled color={colors.green400} fontSize={24} />
                  <Text variant="h2">訂單完成</Text>
                </HStack>
                <CartReminder basePath={basePath} />
                <OrderInfo
                  orderId={paymentTransaction?.transaction_id}
                  formatCreatedDate={formatCreatedDate}
                  purchaseOrderName={paymentTransaction?.purchase_order_name}
                  receiverName={paymentTransaction?.receiver_name}
                  childOrders={paymentTransaction?.child_orders}
                  paymentMethod={paymentMethod}
                  basePath={basePath}
                  refundStatus={paymentTransaction?.refund_status}
                />
                <MerchantInfo
                  merchantName={paymentTransaction?.receiver_name}
                  email={paymentTransaction?.receiver_email}
                  phone={paymentTransaction?.receiver_phone}
                  rootStyle={styles.infoBox}
                />
                <ShippingInfo
                  shippingMethodTitle={shippingMethod}
                  shippingAddress={paymentTransaction?.shipping_address_1}
                  shippingPhone={paymentTransaction?.receiver_phone}
                  shippingName={paymentTransaction?.receiver_name}
                  rootStyle={styles.infoBox}
                  countryCode={paymentTransaction?.country_code}
                />
                <PaymentInfo
                  shouldShowPdf
                  paymentMethod={paymentTransaction?.payment_method?.type}
                  needPdf={paymentTransaction?.need_pdf}
                  postingDateInNextMonth={paymentTransaction?.posting_date_in_next_month}
                  paymentInfo={paymentTransaction?.payment_info}
                  rootStyle={styles.infoBox}
                />
                <InvoiceInfo
                  receiptType={paymentTransaction?.receipt_type}
                  businessUbnTitle={invoiceOption?.business_ubn_title}
                  businessUbn={invoiceOption?.business_ubn}
                  phoneCarrierNumber={invoiceOption?.phone_carrier_number}
                  charityOrganization={invoiceOption?.charity_organization}
                  rootStyle={styles.infoBox}
                  countryCode={paymentTransaction?.country_code}
                />
                <OrderNote
                  deliveryTimeSlot={paymentTransaction?.delivery_time_slot}
                  deliveryDate={paymentTransaction?.delivery_date}
                  customerNote={paymentTransaction?.customer_note}
                  rootStyle={styles.infoBox}
                />
                <AmountDetails
                  subtotal={subtotal}
                  discountTotal={discountTotal}
                  shippingTotal={shippingTotal}
                  total={total}
                  currency={currency}
                  rootStyle={styles.infoBox}
                  showReferralCodeDiscount={false}
                  tax={paymentTransaction?.tax}
                  countryCode={paymentTransaction?.country_code}
                  discountItems={discountItems}
                />
                <Box rootStyle={isMobile && styles.footer}>
                  <Button fullWidth onClick={handleClickBackToHome}>回到首頁</Button>
                </Box>
              </VStack>
            </Box>
          )
      }
    </Box>
  )
}
